<template>
  <div ref="trendChart" class="trend-chart-container">
    <DashboardLoading
      :visible="initLoading && loading"
      text="加载印量与收益数据"
      size="normal"
    />
  </div>

</template>

<script>
import * as echarts from 'echarts'
import { getPrintRevenueTrend } from '@/api/dashboard'
import DashboardLoading from './DashboardLoading.vue'

export default {
  name: 'TrendChart',
  components: {
    DashboardLoading
  },
  props: {
    fontSizes: {
      type: Object,
      default: () => ({
        titleFontSize: 16,
        legendFontSize: 12,
        axisLabelFontSize: 10,
        axisNameFontSize: 12,
        tooltipFontSize: 14,
        dataLabelFontSize: 11,
        smallFontSize: 8,
        largeFontSize: 18
      })
    }
  },
  data () {
    return {
      chartInstance: null,
      refreshTimer: null,
      loading: false,
      trendData: [],
      summaryData: null,
      initLoading: true
    }
  },
  async mounted () {
    await this.fetchPrintRevenueTrend()
    this.initChart()
    // 每10分钟刷新一次数据
    this.refreshTimer = setInterval(() => {
      console.log('重新初始化印量与收益走势数据', this.$moment(new Date(), 'YYYY-MM-DD HH:mm:ss'))
      this.fetchPrintRevenueTrend()
      this.initChart()
    }, 600000)
  },
  beforeDestroy () {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    this.destroyChart()
  },
  watch: {
    fontSizes: {
      handler () {
        if (this.chartInstance) {
          this.updateChartOption()
        }
      },
      deep: true
    }
  },
  methods: {
    async fetchPrintRevenueTrend () {
      try {
        this.loading = true
        const response = await getPrintRevenueTrend()
        console.log('response===>', response)
        if (response.data.status && response.data.data) {
          this.trendData = this.validateAndProcessTrendData(response.data.data.trend || [])
          this.summaryData = response.data.data.summary || null
        }
      } catch (error) {
        console.error('获取印量与收益走势数据失败:', error)
        // 如果接口失败，使用模拟数据
        this.trendData = []
        this.summaryData = null
      } finally {
        this.initLoading = false
        this.loading = false
      }
    },
    validateAndProcessTrendData (data) {
      if (!Array.isArray(data) || data.length === 0) {
        return []
      }

      // 验证和处理数据
      return data.filter(item => {
        return item &&
          typeof item.month === 'string' &&
          typeof item.pages === 'number' &&
          typeof item.revenue === 'number' &&
          item.pages >= 0 &&
          item.revenue >= 0
      }).sort((a, b) => {
        // 按年月排序
        const aDate = new Date(a.year, a.monthNum - 1)
        const bDate = new Date(b.year, b.monthNum - 1)
        return aDate - bDate
      })
    },
    getChartData () {
      if (this.trendData && this.trendData.length > 0) {
        return {
          months: this.trendData.map(item => item.month),
          printVolumeData: this.trendData.map(item => Math.round(item.pages)), // 直接使用实际页数
          revenueData: this.trendData.map(item => Math.round(item.revenue)) // 直接使用实际金额
        }
      } else {
        // 使用模拟数据
        /* return {
          months: ['2024-10', '2024-11', '2024-12', '2025-01', '2025-02', '2025-03', '2025-04', '2025-05', '2025-06', '2025-07', '2025-08', '2025-09'],
          printVolumeData: [68000, 72000, 102000, 156000, 198000, 225000, 267000, 296000, 278000, 245000, 186000, 102000], // 印量数据（实际页数）
          revenueData: [32000, 38000, 52000, 89000, 125000, 168000, 189000, 196000, 189000, 156000, 112000, 68000] // 收益数据（实际金额）
        } */
      }
    },
    initChart () {
      this.$nextTick(() => {
        const chartContainer = this.$refs.trendChart
        if (!chartContainer) return

        // 销毁已存在的图表实例
        if (this.chartInstance) {
          this.chartInstance.dispose()
          this.chartInstance = null
        }

        // 初始化图表
        const chart = echarts.init(chartContainer)

        // 获取图表数据
        const chartData = this.getChartData()
        console.log('chartData===>', chartData)
        const { months, printVolumeData, revenueData } = chartData

        // 科技感配置项
        const option = {
          backgroundColor: 'transparent',
          grid: {
            left: '2%',
            right: '2%',
            top: '10%',
            bottom: '10%',
            containLabel: true
          },
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(0, 30, 80, 0.9)',
            borderColor: '#0096ff',
            borderWidth: 1,
            textStyle: {
              color: '#b8d4f0',
              fontSize: this.fontSizes.tooltipFontSize
            },
            formatter: (params) => {
              let result = `<div style="padding: 8px;">`
              result += `<div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params[0].axisValue}</div>`
              params.forEach(param => {
                const color = param.seriesName === '印量' ? '#0096ff' : '#00ff88'
                let value, unit
                if (param.seriesName === '印量') {
                  value = param.value.toLocaleString() // 直接显示实际页数
                  unit = '页'
                } else {
                  value = param.value.toLocaleString() // 直接显示实际金额
                  unit = '元'
                }
                result += `<div>${param.seriesName}: <span style="color: ${color};">${value}${unit}</span></div>`
              })
              result += `</div>`
              return result
            },
            extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
          },
          legend: {
            data: ['印量', '收益'],
            textStyle: {
              color: '#b8d4f0',
              fontSize: this.fontSizes.legendFontSize
            },
            top: '1%',
            right: '5%',
            itemWidth: 16,
            itemHeight: 6
          },
          xAxis: {
            type: 'category',
            data: months,
            axisLine: {
              lineStyle: {
                color: 'rgba(0, 150, 255, 0.3)'
              }
            },
            axisTick: {
              lineStyle: {
                color: 'rgba(0, 150, 255, 0.3)'
              }
            },
            axisLabel: {
              color: '#87ceeb',
              fontSize: this.fontSizes.axis
            },
            splitLine: {
              show: false
            }
          },
          yAxis: {
            type: 'value',
            name: '数值',
            nameTextStyle: {
              color: '#0096ff',
              fontSize: this.fontSizes.smallFontSize
            },
            axisLine: {
              lineStyle: {
                color: 'rgba(0, 150, 255, 0.3)'
              }
            },
            axisTick: {
              lineStyle: {
                color: 'rgba(0, 150, 255, 0.3)'
              }
            },
            axisLabel: {
              color: '#87ceeb',
              fontSize: this.fontSizes.axisLabelFontSize,
              formatter: function (value) {
                // 直接显示实际数值，根据大小添加单位
                if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(0) + 'K'
                } else {
                  return value.toString()
                }
              }
            },
            splitLine: {
              show: true,
              lineStyle: {
                color: 'rgba(0, 150, 255, 0.2)',
                type: 'dashed',
                width: 1
              }
            }
          },
          series: [
            {
              name: '印量',
              type: 'line',
              data: printVolumeData,
              smooth: false,
              lineStyle: {
                color: '#0096ff',
                width: 1,
                shadowColor: 'rgba(0, 150, 255, 0.3)',
                shadowBlur: 10
              },
              itemStyle: {
                color: '#0096ff',
                borderColor: '#ffffff',
                borderWidth: 2
              },
              symbol: 'circle',
              symbolSize: 6,
              emphasis: {
                itemStyle: {
                  color: '#ffffff',
                  borderColor: '#0096ff',
                  borderWidth: 3,
                  shadowColor: 'rgba(0, 150, 255, 0.8)',
                  shadowBlur: 15
                },
                scale: 1.5
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(0, 150, 255, 0.3)' },
                    { offset: 1, color: 'rgba(0, 150, 255, 0.05)' }
                  ]
                }
              }
            },
            {
              name: '收益',
              type: 'line',
              data: revenueData,
              smooth: false,
              lineStyle: {
                color: '#00ff88',
                width: 1,
                shadowColor: 'rgba(0, 255, 136, 0.3)',
                shadowBlur: 10
              },
              itemStyle: {
                color: '#00ff88',
                borderColor: '#ffffff',
                borderWidth: 2
              },
              symbol: 'circle',
              symbolSize: 6,
              emphasis: {
                itemStyle: {
                  color: '#ffffff',
                  borderColor: '#00ff88',
                  borderWidth: 3,
                  shadowColor: 'rgba(0, 255, 136, 0.8)',
                  shadowBlur: 15
                },
                scale: 1.5
              },
              areaStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: 'rgba(0, 255, 136, 0.3)' },
                    { offset: 1, color: 'rgba(0, 255, 136, 0.05)' }
                  ]
                }
              }
            }
          ],
          animationDuration: 2000,
          animationEasing: 'cubicInOut'
        }

        // 设置配置项并渲染图表
        chart.setOption(option)

        // 保存图表实例
        this.chartInstance = chart

        // 响应式调整
        this.resizeHandler = () => {
          if (this.chartInstance && !this.chartInstance.isDisposed()) {
            this.chartInstance.resize()
          }
        }

        window.addEventListener('resize', this.resizeHandler)
      })
    },
    updateChartOption () {
      if (!this.chartInstance || this.chartInstance.isDisposed()) {
        this.initChart()
        return
      }

      // 获取图表数据
      const chartData = this.getChartData()
      const { months, printVolumeData, revenueData } = chartData

      const option = {
        backgroundColor: 'transparent',
        grid: {
          left: '2%',
          right: '2%',
          top: '10%',
          bottom: '10%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 30, 80, 0.9)',
          borderColor: '#0096ff',
          borderWidth: 1,
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.tooltipFontSize
          },
          formatter: (params) => {
            let result = `<div style="padding: 8px;">`
            result += `<div style="color: #0096ff; font-weight: bold; margin-bottom: 5px;">${params[0].axisValue}</div>`
            params.forEach(param => {
              const color = param.seriesName === '印量' ? '#0096ff' : '#00ff88'
              let value, unit
              if (param.seriesName === '印量') {
                value = param.value.toLocaleString() // 直接显示实际页数
                unit = '页'
              } else {
                value = param.value.toLocaleString() // 直接显示实际金额
                unit = '元'
              }
              result += `<div>${param.seriesName}: <span style="color: ${color};">${value}${unit}</span></div>`
            })
            result += `</div>`
            return result
          },
          extraCssText: 'box-shadow: 0 0 20px rgba(0, 150, 255, 0.3); border-radius: 8px;'
        },
        legend: {
          data: ['印量', '收益'],
          textStyle: {
            color: '#b8d4f0',
            fontSize: this.fontSizes.legendFontSize
          },
          top: '1%',
          right: '5%',
          itemWidth: 16,
          itemHeight: 6
        },
        xAxis: {
          type: 'category',
          data: months,
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.3)'
            }
          },
          axisTick: {
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.3)'
            }
          },
          axisLabel: {
            color: '#87ceeb',
            fontSize: this.fontSizes.axisLabelFontSize
          },
          splitLine: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: '数值',
          nameTextStyle: {
            color: '#0096ff',
            fontSize: this.fontSizes.smallFontSize
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.3)'
            }
          },
          axisTick: {
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.3)'
            }
          },
          axisLabel: {
            color: '#87ceeb',
            fontSize: this.fontSizes.axisLabelFontSize
          },
          splitLine: {
            show: true,
            lineStyle: {
              color: 'rgba(0, 150, 255, 0.2)',
              type: 'dashed',
              width: 1
            }
          }
        },
        series: [
          {
            name: '印量',
            type: 'line',
            data: printVolumeData,
            smooth: false,
            lineStyle: {
              color: '#0096ff',
              width: 1,
              shadowColor: 'rgba(0, 150, 255, 0.3)',
              shadowBlur: 10
            },
            itemStyle: {
              color: '#0096ff',
              borderColor: '#ffffff',
              borderWidth: 2
            },
            symbol: 'circle',
            symbolSize: 6,
            emphasis: {
              itemStyle: {
                color: '#ffffff',
                borderColor: '#0096ff',
                borderWidth: 3,
                shadowColor: 'rgba(0, 150, 255, 0.8)',
                shadowBlur: 15
              },
              scale: 1.5
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(0, 150, 255, 0.3)' },
                  { offset: 1, color: 'rgba(0, 150, 255, 0.05)' }
                ]
              }
            }
          },
          {
            name: '收益',
            type: 'line',
            data: revenueData,
            smooth: false,
            lineStyle: {
              color: '#00ff88',
              width: 1,
              shadowColor: 'rgba(0, 255, 136, 0.3)',
              shadowBlur: 10
            },
            itemStyle: {
              color: '#00ff88',
              borderColor: '#ffffff',
              borderWidth: 2
            },
            symbol: 'circle',
            symbolSize: 6,
            emphasis: {
              itemStyle: {
                color: '#ffffff',
                borderColor: '#00ff88',
                borderWidth: 3,
                shadowColor: 'rgba(0, 255, 136, 0.8)',
                shadowBlur: 15
              },
              scale: 1.5
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: 'rgba(0, 255, 136, 0.3)' },
                  { offset: 1, color: 'rgba(0, 255, 136, 0.05)' }
                ]
              }
            }
          }
        ],
        animationDuration: 2000,
        animationEasing: 'cubicInOut'
      }

      this.chartInstance.setOption(option, true)
    },
    destroyChart () {
      // 清理resize事件监听器
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
      // 销毁图表实例
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
    }
  }
}
</script>

<style scoped>
.trend-chart-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
}
</style>
